/**
 * Error handling utilities for API calls
 */

/**
 * Returns demo patient data for fallback purposes
 * @returns {Array} Array of demo patients
 */
export const getDemoPatients = () => [
  {
    id: 'P001',
    firstName: 'John',
    lastName: 'Doe',
    surgeryType: 'Knee Replacement',
    dob: '1970-01-01T00:00:00.000Z',
    _source: 'demo',
  },
  {
    id: 'P002',
    firstName: 'Jane',
    lastName: 'Smith',
    surgeryType: 'Hip Replacement',
    dob: '1980-05-15T00:00:00.000Z',
    _source: 'demo',
  },
  {
    id: 'P003',
    firstName: 'Robert',
    lastName: 'Johnson',
    surgeryType: 'Spinal Fusion',
    dob: '1965-09-23T00:00:00.000Z',
    _source: 'demo',
  },
];

/**
 * Returns a demo patient by ID
 * @param {string} patientId - The patient ID to look up
 * @returns {Object} A demo patient
 */
export const getDemoPatient = patientId => {
  // First check localStorage for any persisted patient data
  try {
    const storedPatientsStr = localStorage.getItem('demoPatients');
    if (storedPatientsStr) {
      const storedPatients = JSON.parse(storedPatientsStr);
      if (Array.isArray(storedPatients)) {
        const storedPatient = storedPatients.find(p => p.id === patientId);
        if (storedPatient) {
          console.log(`[errorHandling] Found patient ${patientId} in localStorage`);
          return {
            ...storedPatient,
            _source: 'demo',
          };
        }
      }
    }
  } catch (storageError) {
    console.warn(
      `[errorHandling] Error reading patient ${patientId} from localStorage:`,
      storageError
    );
    // Continue with fallback data if localStorage fails
  }

  // Define enhanced demo patients with complete data
  const enhancedPatients = {
    P001: {
      id: 'P001',
      firstName: 'John',
      lastName: 'Doe',
      surgeryType: 'Knee Replacement',
      dob: '1970-01-01T00:00:00.000Z',
      gender: 'M',
      height: 180,
      weight: 85,
      bmi: 26.2,
      asaScore: '2',
      comorbidities: ['Hypertension', 'Type 2 Diabetes'],
      medications: ['Lisinopril', 'Metformin', 'Aspirin'],
      allergies: ['Penicillin', 'Sulfa drugs'],
      labs: {
        hemoglobin: 14.5,
        hematocrit: 43,
        platelets: 250,
        na: 140,
        k: 4.2,
        hco3: 24,
        creatinine: 0.9,
        glu: 110,
        inr: 1.0,
      },
      _source: 'demo',
    },
    P002: {
      id: 'P002',
      firstName: 'Jane',
      lastName: 'Smith',
      surgeryType: 'Hip Replacement',
      dob: '1980-05-15T00:00:00.000Z',
      gender: 'F',
      height: 165,
      weight: 70,
      bmi: 25.7,
      asaScore: '1',
      comorbidities: ['Osteoarthritis'],
      medications: ['Acetaminophen', 'Ibuprofen'],
      allergies: ['Codeine'],
      labs: {
        hemoglobin: 13.8,
        hematocrit: 41,
        platelets: 280,
        na: 138,
        k: 4.0,
        hco3: 25,
        creatinine: 0.8,
        glu: 95,
        inr: 1.1,
      },
      _source: 'demo',
    },
    P003: {
      id: 'P003',
      firstName: 'Robert',
      lastName: 'Johnson',
      surgeryType: 'Spinal Fusion',
      dob: '1965-09-23T00:00:00.000Z',
      gender: 'M',
      height: 175,
      weight: 90,
      bmi: 29.4,
      asaScore: '3',
      comorbidities: ['Hypertension', 'COPD', 'Obesity'],
      medications: ['Amlodipine', 'Albuterol', 'Fluticasone'],
      allergies: ['NSAIDs', 'Tetracyclines'],
      labs: {
        hemoglobin: 15.2,
        hematocrit: 46,
        platelets: 220,
        na: 139,
        k: 4.5,
        hco3: 28,
        creatinine: 1.1,
        glu: 105,
        inr: 1.2,
      },
      _source: 'demo',
    },
  };

  // Return the matching enhanced patient or a generic one if ID doesn't match
  return (
    enhancedPatients[patientId] || {
      id: patientId || 'unknown',
      firstName: 'Demo',
      lastName: 'Patient',
      surgeryType: 'General Surgery',
      dob: '1975-06-15T00:00:00.000Z',
      gender: 'M',
      height: 178,
      weight: 80,
      bmi: 25.2,
      asaScore: '2',
      comorbidities: ['Hypertension'],
      medications: ['Lisinopril', 'Aspirin'],
      allergies: ['Penicillin'],
      labs: {
        hemoglobin: 14.0,
        hematocrit: 42,
        platelets: 230,
        na: 140,
        k: 4.1,
        hco3: 24,
        creatinine: 1.0,
        glu: 100,
        inr: 1.0,
      },
      _source: 'demo',
    }
  );
};

/**
 * Returns demo surgeon data for fallback purposes
 * @returns {Array} Array of demo surgeons
 */
export const getDemoSurgeons = () => [
  { id: 1, name: 'Dr. Smith', specialty: 'Orthopedics' },
  { id: 2, name: 'Dr. Johnson', specialty: 'Cardiology' },
  { id: 3, name: 'Dr. Williams', specialty: 'Neurology' },
];

/**
 * Creates a standard API request configuration with CORS handling
 * @returns {Object} Axios request configuration
 */
export const createApiRequestConfig = () => ({
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  // Important for CORS handling
  withCredentials: false,
});

/**
 * Determines if the application is running locally or in production
 * @returns {boolean} True if running locally, false if in production
 */
export const isRunningLocally = () => {
  return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
};

/**
 * Handles API response data, providing fallback data if necessary
 * @param {Object} response - The API response
 * @param {Function} fallbackDataFn - Function to provide fallback data
 * @param {Function} normalizeDataFn - Function to normalize the data
 * @returns {any} The processed data
 */
export const handleApiResponse = (response, fallbackDataFn, normalizeDataFn) => {
  // If we have a valid response with data
  if (response && response.data) {
    // Check if data is an array
    if (Array.isArray(response.data)) {
      return normalizeDataFn ? response.data.map(normalizeDataFn) : response.data;
    }

    // Check if data is nested in a data property
    if (response.data.data && Array.isArray(response.data.data)) {
      return normalizeDataFn ? response.data.data.map(normalizeDataFn) : response.data.data;
    }

    // If it's a single object
    if (typeof response.data === 'object') {
      return normalizeDataFn ? normalizeDataFn(response.data) : response.data;
    }
  }

  // If we can't process the data, use fallback
  console.warn('[API] Response data is not in expected format, using fallback data');
  return fallbackDataFn ? fallbackDataFn() : null;
};

/**
 * Handles API errors, providing fallback data in production
 * @param {Error} error - The API error
 * @param {Function} fallbackDataFn - Function to provide fallback data
 * @returns {any} The fallback data or throws the error
 */
export const handleApiError = (error, fallbackDataFn) => {
  console.error('[API] Error:', error);

  // In production, use fallback data
  if (!isRunningLocally()) {
    console.warn('[API] Using fallback data due to error');
    return fallbackDataFn ? fallbackDataFn() : null;
  }

  // In development, throw the error
  throw error;
};
